import React, { useState, useEffect } from 'react'
import Button from '@/components/ui/Button'
import Tag from '@/components/ui/Tag'
import SidePanel from '@/components/ui/SidePanel'
import { designTokens } from '@/utils/design-tokens'
import { PerformanceCategoryService } from '@/api/generated/services/PerformanceCategoryService'
import { PerformanceCategoryCreateRequest } from '@/api/generated/models/PerformanceCategoryCreateRequest'
import { useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'
import RequiredLabel from '@/components/ui/RequiredLabel'

interface CategoryCreatePanelProps {
  isOpen: boolean
  onClose: () => void
  parentId: string
  parentName: string
  groupId: string
  groupName: string
  categoryType: '분류'
  isDirectUnderGroup?: boolean  // 지표구분 바로 하위 분류인지 여부
  onSuccess?: () => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{
  label: string | React.ReactNode,
  value: string | React.ReactNode,
  fullWidth?: boolean
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span 
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}
      >
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span 
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '16px',
            fontWeight: 400,
            color: designTokens.colors.gray[6]
          }}
        >
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const CategoryCreatePanel: React.FC<CategoryCreatePanelProps> = ({
  isOpen,
  onClose,
  parentId,
  parentName,
  groupId,
  groupName,
  categoryType,
  isDirectUnderGroup = false,
  onSuccess
}) => {
  const { showError } = useAlert()
  
  // Form state
  const [categoryName, setCategoryName] = useState('')
  const [manualWeight, setManualWeight] = useState<number | null>(null)
  const [isManualWeight, setIsManualWeight] = useState(false)

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Reset form when panel opens/closes
  useEffect(() => {
    if (isOpen) {
      setCategoryName('')
      setManualWeight(null)
      setIsManualWeight(false)
    }
  }, [isOpen])

  const handleSubmit = async () => {
    if (!groupId) {
      showError('지표구분이 선택되지 않았습니다.')
      return
    }

    if (!categoryName.trim()) {
      showError(`${categoryType}명을 입력해주세요.`)
      return
    }


    setIsSubmitting(true)

    try {
      const createRequest: PerformanceCategoryCreateRequest = {
        group_id: groupId,
        // 지표구분 바로 하위 분류인 경우 parent_id를 제외
        ...(isDirectUnderGroup ? {} : { parent_id: parentId }),
        name: categoryName.trim(),
        // 수동 가중치가 설정된 경우에만 포함
        ...(isManualWeight && manualWeight !== null ? { manual_weight: manualWeight } : {}),
      }

      await PerformanceCategoryService.appsPerformanceViewsCreatePerformanceCategory({
        requestBody: createRequest
      })

      // Success
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error(`Failed to create ${categoryType}:`, err)
      handleApiError(err, showError, `${categoryType} ${ErrorMessages.CREATE_FAILED}`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (isSubmitting) return
    onClose()
  }

  const isFormValid = categoryName.trim()

  const headerActions = (
    <>
      <Button
        size="small"
        color="white"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        취소
      </Button>
      <Button
        size="small"
        color="dark"
        onClick={handleSubmit}
        disabled={isSubmitting || !isFormValid}
      >
        {isSubmitting ? '저장 중...' : '저장'}
      </Button>
    </>
  )

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={handleCancel}
      title={`${categoryType} 생성`}
      width={800}
      headerActions={headerActions}
    >
      <div 
        className="flex flex-col space-y-6" 
        style={{ 
          padding: '24px',
        }}
      >

        {/* Section 1: 기본정보 */}
        <div className="space-y-4">
          <SectionHeader title="기본정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField 
              label="지표구분" 
              value={
                <Tag color="purple" size="small">
                  ● {groupName}
                </Tag>
              }
            />
            <InfoField 
              label="유형" 
              value={
                <Tag color="info" size="small">
                  ● {categoryType}
                </Tag>
              }
            />
            <InfoField 
              label="상위 분류" 
              value={parentName} 
            />
          </div>
        </div>

        {/* Section 2: 분류 정보 */}
        <div className="space-y-4">
          <SectionHeader title="하위분류 정보" />
          <div className="space-y-4" style={{ padding: '0px 4px' }}>
            <InfoField
              label={<RequiredLabel>하위분류명</RequiredLabel>}
              fullWidth={true}
              value={
                <input
                  type="text"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  placeholder={`${categoryType}명을 입력하세요`}
                  className="w-full p-2 border rounded"
                  style={{
                    borderColor: designTokens.colors.gray[1],
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '16px',
                    color: designTokens.colors.gray[6]
                  }}
                  disabled={isSubmitting}
                />
              }
            />

            {/* 수동 가중치 설정 */}
            <InfoField
              label="가중치 설정"
              fullWidth={true}
              value={
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isManualWeight}
                      onChange={(e) => {
                        setIsManualWeight(e.target.checked)
                        if (!e.target.checked) {
                          setManualWeight(null)
                        }
                      }}
                      disabled={isSubmitting}
                      className="rounded"
                    />
                    <span style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '14px',
                      color: designTokens.colors.gray[6]
                    }}>
                      수동 가중치 사용
                    </span>
                  </label>

                  {isManualWeight && (
                    <input
                      type="number"
                      value={manualWeight || ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseFloat(e.target.value)
                        setManualWeight(value)
                      }}
                      placeholder="가중치를 입력하세요 (0-100)"
                      min="0"
                      max="100"
                      step="0.1"
                      className="w-full p-2 border rounded"
                      style={{
                        borderColor: designTokens.colors.gray[1],
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        color: designTokens.colors.gray[6]
                      }}
                      disabled={isSubmitting}
                    />
                  )}

                  <p style={{
                    fontFamily: designTokens.typography.fontFamily.sans[0],
                    fontSize: '12px',
                    color: designTokens.colors.gray[4]
                  }}>
                    {isManualWeight
                      ? '수동으로 설정된 가중치가 적용됩니다.'
                      : '자동으로 계산된 가중치가 적용됩니다.'
                    }
                  </p>
                </div>
              }
            />

          </div>
        </div>
      </div>
    </SidePanel>
  )
}

export default CategoryCreatePanel